import { useMemo } from 'react';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { useProfileTheme } from '@/hooks/useProfileTheme';
import { DEFAULT_THEME, type ProfileTheme } from '@/lib/profileTheme';
import type { NostrMetadata } from '@nostrify/nostrify';

export interface ResolvedIdentifier {
  pubkey: string;
  npub: string;
}

/**
 * Decode an npub or nprofile string into a hex pubkey + normalized npub.
 * Returns null for invalid or unsupported identifiers.
 */
export function resolveIdentifier(input: string): ResolvedIdentifier | null {
  const trimmed = input.trim().replace(/^nostr:/, '');
  if (!trimmed) return null;
  try {
    const decoded = nip19.decode(trimmed);
    if (decoded.type === 'npub') {
      return { pubkey: decoded.data, npub: trimmed };
    }
    if (decoded.type === 'nprofile') {
      return { pubkey: decoded.data.pubkey, npub: nip19.npubEncode(decoded.data.pubkey) };
    }
  } catch {
    // Maybe it's a raw hex pubkey
    if (/^[0-9a-f]{64}$/i.test(trimmed)) {
      const hex = trimmed.toLowerCase();
      return { pubkey: hex, npub: nip19.npubEncode(hex) };
    }
  }
  return null;
}

export interface CardDataResult {
  metadata?: NostrMetadata;
  theme: ProfileTheme;
  isLoading: boolean;
  hasTheme: boolean;
  themeTitle?: string;
}

/**
 * Combines the kind 0 profile and kind 16767 active theme for a pubkey.
 */
export function useCardData(pubkey: string | undefined): CardDataResult {
  const author = useAuthor(pubkey);
  const themeQuery = useProfileTheme(pubkey);

  return useMemo(() => {
    const theme = themeQuery.data ?? DEFAULT_THEME;
    return {
      metadata: author.data?.metadata,
      theme,
      isLoading: author.isLoading || themeQuery.isLoading,
      hasTheme: !!themeQuery.data,
      themeTitle: themeQuery.data?.title,
    };
  }, [author.data, author.isLoading, themeQuery.data, themeQuery.isLoading]);
}
